import { decode, type DecodeResult } from "./decoder";
import { cropImageData, estimateBarAngle, rotateImageData } from "./geometry";
import { locateBarcodes } from "./locate";
import { meanLuma } from "./luma";
import type { Symbology } from "./formats";

/**
 * The full per-frame decode pipeline, shared verbatim by the Web Worker and
 * the inline fallback: sharpness gate → direct decode → locator rescue with
 * deskew retry. Pure ImageData in, results in frame coordinates out.
 */

export interface PipelineOutcome {
  results: DecodeResult[];
  /** Locator saw bar-like regions (drives the move-closer hint). */
  sawRegions: boolean;
  /** Mean frame luma (drives auto-torch / low-light hint). */
  luma: number;
  /** Frame was skipped as too blurry to be worth a decode attempt. */
  skippedBlurry: boolean;
}

export interface PipelineState {
  /** Rolling sharpness ceiling; decays so scene changes re-adapt. */
  sharpnessMax: number;
}

export function createPipelineState(): PipelineState {
  return { sharpnessMax: 0 };
}

/** Skip decoding when sharpness falls below this fraction of the ceiling. */
const GATE_RATIO = 0.4;
/** Ceilings below this mean "no sharp frame seen yet" — never gate. */
const GATE_FLOOR = 6;
/** Deskew retry only beyond this tilt (radians ≈ 5°). */
const DESKEW_MIN_ANGLE = 0.087;

/** Sampled mean |horizontal gradient| — cheap sharpness proxy. */
function sharpnessOf(img: ImageData): number {
  const { width: w, height: h, data } = img;
  let sum = 0;
  let n = 0;
  // Dense in x (stride-aligned sampling can resonate with bar pitch and
  // read zero sharpness on a perfectly sharp barcode), sparse in y.
  const stepY = Math.max(1, Math.floor(h / 24));
  for (let y = 0; y < h; y += stepY) {
    const row = y * w;
    for (let x = 1; x < w - 1; x++) {
      sum += Math.abs(data[(row + x + 1) * 4 + 1] - data[(row + x - 1) * 4 + 1]);
      n++;
    }
  }
  return n > 0 ? sum / n : 0;
}

export async function runPipeline(
  image: ImageData,
  symbologies: Symbology[],
  thorough: boolean,
  state: PipelineState,
): Promise<PipelineOutcome> {
  const luma = meanLuma(image);
  const sharpness = sharpnessOf(image);
  const gated =
    state.sharpnessMax > GATE_FLOOR &&
    sharpness < state.sharpnessMax * GATE_RATIO;
  state.sharpnessMax = Math.max(sharpness, state.sharpnessMax * 0.98);
  if (gated) {
    return { results: [], sawRegions: false, luma, skippedBlurry: true };
  }

  let results = await decode(image, symbologies, { thorough });

  // Locator rescue for regions the direct pass missed.
  const rects = locateBarcodes(image, 3);
  const sawRegions = rects.length > 0;
  for (const rect of rects) {
    const cx = Math.round(rect.x * image.width);
    const cy = Math.round(rect.y * image.height);
    const cw = Math.round(rect.width * image.width);
    const ch = Math.round(rect.height * image.height);
    if (cw < 32 || ch < 16 || (cw >= image.width && ch >= image.height)) continue;
    const covered = results.some((r) =>
      r.points.some((p) => p.x >= cx && p.x <= cx + cw && p.y >= cy && p.y <= cy + ch),
    );
    if (covered) continue;

    const crop = cropImageData(image, cx, cy, cw, ch);
    let cropResults = await decode(crop, symbologies, { thorough: true });

    if (cropResults.length === 0) {
      // Tilted labels: measure bar tilt and retry on a deskewed crop.
      const angle = estimateBarAngle(crop);
      if (Math.abs(angle) > DESKEW_MIN_ANGLE) {
        cropResults = await decode(rotateImageData(crop, -angle), symbologies, {
          thorough: true,
        });
      }
    }

    for (const r of cropResults) {
      if (results.some((f) => f.data === r.data)) continue;
      // Deskewed coords are approximate; anchor to the crop for highlights.
      results = results.concat({
        ...r,
        points: r.points.map((p) => ({ x: p.x + cx, y: p.y + cy })),
      });
    }
  }

  return { results, sawRegions, luma, skippedBlurry: false };
}
